import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Play, RefreshCcw, Save, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { usePageParams } from '@/hooks/usePageParams';

type CycleCountLine = {
  id: string;
  line_number?: number | null;
  inventory_code?: string | null;
  sku_code?: string | null;
  description?: string | null;
  bin_code?: string | null;
  system_qty?: number | null;
  counted_qty?: number | null;
  variance_reason?: string | null;
  status?: string | null;
  line_length?: string | null;
};

type CycleCountDetail = {
  id: string;
  count_number?: string | null;
  count_type?: string | null;
  count_mode?: string | null;
  scope_type?: string | null;
  status?: string | null;
  created_at?: string | null;
  planned_date?: string | null;
  notes?: string | null;
  lines: CycleCountLine[];
};

type LineDraft = {
  countedQty: string;
  remark: string;
};

type Props = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
};

function formatStatusLabel(status: string | null | undefined) {
  if (!status) return 'Unknown';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusTone(status: string | null | undefined) {
  switch (status) {
    case 'APPROVED':
    case 'ADJUSTED':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'IN_PROGRESS':
    case 'REVIEWING':
    case 'PENDING_APPROVAL':
    case 'COUNTED':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB').format(date);
}

function createDraft(line: CycleCountLine): LineDraft {
  return {
    countedQty: line.counted_qty == null ? '' : String(line.counted_qty),
    remark: line.variance_reason ?? '',
  };
}

function HeaderField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 min-h-8 text-sm font-medium text-foreground">{value && value.trim() ? value : '-'}</div>
    </div>
  );
}

export function WmsCycleCountDetailPanel({ id: idProp }: Props) {
  const params = usePageParams<{ id: string }>();
  const id = idProp ?? params.id;
  const [detail, setDetail] = useState<CycleCountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [error, setError] = useState('');

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<CycleCountDetail>(`/wms/cycle-counts/${encodeURIComponent(id)}`);
      setDetail(res);
      setDrafts(
        Object.fromEntries((res.lines ?? []).map((line) => [line.id, createDraft(line)])),
      );
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load stock take detail');
      setDetail(null);
      setDrafts({});
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const dirtyLineIds = useMemo(() => {
    if (!detail) return [];
    return detail.lines
      .filter((line) => {
        const draft = drafts[line.id] ?? createDraft(line);
        return draft.countedQty !== (line.counted_qty == null ? '' : String(line.counted_qty))
          || draft.remark !== (line.variance_reason ?? '');
      })
      .map((line) => line.id);
  }, [detail, drafts]);

  const status = String(detail?.status ?? '').toUpperCase();
  const canEditLines = ['PLANNED', 'IN_PROGRESS'].includes(status);

  const saveDirtyLines = async () => {
    if (!detail || dirtyLineIds.length === 0) return true;
    setSaving(true);
    try {
      for (const lineId of dirtyLineIds) {
        const line = detail.lines.find((entry) => entry.id === lineId);
        const draft = drafts[lineId];
        if (!line || !draft) continue;
        await api.patch(`/wms/cycle-counts/${encodeURIComponent(detail.id)}/lines/${encodeURIComponent(lineId)}`, {
          countedQty: draft.countedQty === '' ? null : Number(draft.countedQty),
          varianceReason: draft.remark.trim() || null,
        });
      }
      toast.success('Stock take lines saved');
      await fetchDetail();
      return true;
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save stock take lines');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    await saveDirtyLines();
  };

  const startCount = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.patch(`/wms/cycle-counts/${encodeURIComponent(detail.id)}`, { status: 'IN_PROGRESS' });
      toast.success('Stock take started');
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to start stock take');
    } finally {
      setSaving(false);
    }
  };

  const submitForApproval = async () => {
    if (!detail) return;
    const saved = await saveDirtyLines();
    if (!saved) return;
    setSaving(true);
    try {
      await api.patch(`/wms/cycle-counts/${encodeURIComponent(detail.id)}`, { status: 'PENDING_APPROVAL' });
      toast.success('Stock take submitted for approval');
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to submit stock take');
    } finally {
      setSaving(false);
    }
  };

  const approveStockTake = async () => {
    if (!detail) return;
    if (!window.confirm('Approve stock take and apply adjustments?')) return;
    setSaving(true);
    try {
      await api.post(`/wms/cycle-counts/${encodeURIComponent(detail.id)}/approve`, { adjustInventory: true });
      toast.success('Stock take approved');
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to approve stock take');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading stock take detail...
      </div>
    );
  }

  if (error || !detail) {
    return <div className="p-6 text-sm text-destructive">{error || 'Stock take detail not found.'}</div>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="shrink-0 border-b px-6 py-4">
        <div>
          <div className="pr-10">
            <h2 className="text-lg font-semibold tracking-tight">Stock Take : {detail.count_number ?? '-'}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Stock take detail</p>
            <div className="mt-2">
              <Badge className={`border ${statusTone(detail.status)}`}>{formatStatusLabel(detail.status)}</Badge>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-start gap-2">
            {status === 'PLANNED' ? (
              <Button variant="outline" onClick={() => void startCount()} disabled={loading || saving}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
                Start Count
              </Button>
            ) : null}
            {status === 'IN_PROGRESS' ? (
              <Button onClick={() => void submitForApproval()} disabled={loading || saving}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Submit
              </Button>
            ) : null}
            {status === 'REVIEWING' || status === 'PENDING_APPROVAL' ? (
              <Button onClick={() => void approveStockTake()} disabled={loading || saving}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
                Approve
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => void fetchDetail()} disabled={loading || saving}>
              <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button onClick={() => void saveAll()} disabled={saving || !canEditLines || dirtyLineIds.length === 0}>
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
              Save All
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-6 gap-4">
          <HeaderField label="Count No" value={detail.count_number ?? '-'} />
          <HeaderField label="Type" value={formatStatusLabel(detail.count_type)} />
          <HeaderField label="Mode" value={formatStatusLabel(detail.count_mode)} />
          <HeaderField label="Scope" value={formatStatusLabel(detail.scope_type)} />
          <HeaderField label="Schedule Date" value={formatDate(detail.planned_date)} />
          <HeaderField label="Remark" value={detail.notes ?? '-'} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden px-6 py-4">
        <div className="mb-2 text-sm font-semibold">Lines</div>
        <div className="runtime-grid-scrollbar boox-x-scroll h-full overflow-auto rounded-md border">
          <table className="min-w-[1280px] w-full text-sm">
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b bg-slate-50">
                <th className="px-3 py-2 text-left font-medium">Line</th>
                <th className="px-3 py-2 text-left font-medium">Inventory Code</th>
                <th className="px-3 py-2 text-left font-medium">SKU Code</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="px-3 py-2 text-left font-medium">Length</th>
                <th className="px-3 py-2 text-left font-medium">Bin</th>
                <th className="px-3 py-2 text-left font-medium">System Qty</th>
                <th className="px-3 py-2 text-left font-medium">Counted Qty</th>
                <th className="px-3 py-2 text-left font-medium">Remark</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => {
                const draft = drafts[line.id] ?? createDraft(line);
                return (
                  <tr key={line.id} className="border-b align-top">
                    <td className="px-3 py-2">{line.line_number ?? '-'}</td>
                    <td className="px-3 py-2 font-medium">{line.inventory_code ?? '-'}</td>
                    <td className="px-3 py-2">{line.sku_code ?? '-'}</td>
                    <td className="px-3 py-2">{line.description ?? '-'}</td>
                    <td className="px-3 py-2">{line.line_length ?? '-'}</td>
                    <td className="px-3 py-2">{line.bin_code ?? '-'}</td>
                    <td className="px-3 py-2">{Number(line.system_qty ?? 0).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.countedQty}
                        disabled={!canEditLines || saving}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [line.id]: { ...draft, countedQty: event.target.value },
                        }))}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={draft.remark}
                        disabled={!canEditLines || saving}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [line.id]: { ...draft, remark: event.target.value },
                        }))}
                        placeholder="Remark"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`border ${statusTone(line.status)}`}>{formatStatusLabel(line.status)}</Badge>
                    </td>
                  </tr>
                );
              })}
              {detail.lines.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No stock take lines found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WmsCycleCountDetailPanel;
